/**
 * Activity Tracking Service
 * Monitors and logs all user interactions within the system
 * Separate from API cost tracking (Claude, AutoGrab, etc.)
 */

import { supabase } from '../lib/supabase';

export type ModuleType =
  | 'dashboard'
  | 'inventory'
  | 'customers'
  | 'deals'
  | 'workshop'
  | 'intelligence'
  | 'advertising'
  | 'accounting'
  | 'documents'
  | 'reports'
  | 'settings'
  | 'admin'
  | 'finance';

export type ActionType =
  | 'navigate' // Page navigation
  | 'view' // Viewing a resource
  | 'create' // Creating new resource
  | 'update' // Updating existing resource
  | 'delete' // Deleting resource
  | 'export' // Exporting data
  | 'import' // Importing data
  | 'search' // Search operations
  | 'filter' // Applying filters
  | 'download' // Downloading files
  | 'upload' // Uploading files
  | 'print' // Printing documents
  | 'email' // Sending emails
  | 'ai_query' // AI assistant queries (not API cost, just tracking usage)
  | 'report_generate' // Generating reports
  | 'custom'; // Custom actions

export type ResourceType =
  | 'vehicle'
  | 'customer'
  | 'deal'
  | 'task'
  | 'service_record'
  | 'invoice'
  | 'document'
  | 'report'
  | 'user'
  | 'organization'
  | 'setting';

export interface ActivityLog {
  id: string;
  user_id: string;
  organization_id?: string;
  module: ModuleType;
  action: ActionType;
  resource_type?: ResourceType;
  resource_id?: string;
  resource_name?: string;
  metadata?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface UserActivitySummary {
  userId: string;
  totalActivities: number;
  moduleBreakdown: Record<ModuleType, number>;
  actionBreakdown: Record<ActionType, number>;
  mostUsedModules: Array<{ module: ModuleType; count: number }>;
  mostCommonActions: Array<{ action: ActionType; count: number }>;
  recentActivities: ActivityLog[];
  firstActivity: string;
  lastActivity: string;
  activeDays: number;
}

export interface ActivityFilters {
  userId?: string;
  organizationId?: string;
  module?: ModuleType | ModuleType[];
  action?: ActionType | ActionType[];
  resourceType?: ResourceType;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
}

class ActivityTrackingService {
  private currentUserId: string | null = null;
  private currentOrganizationId: string | null = null;
  private isInitialized = false;

  /**
   * Initialize the service with current user context
   */
  async initialize() {
    if (this.isInitialized) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        this.currentUserId = user.id;

        // Get organization ID
        const { data: userData } = await supabase
          .from('users')
          .select('organization_id')
          .eq('id', user.id)
          .single();

        this.currentOrganizationId = userData?.organization_id || null;
        this.isInitialized = true;
      }
    } catch (error) {
      console.error('Failed to initialize activity tracking:', error);
    }
  }

  /**
   * Log an activity
   */
  async logActivity(
    module: ModuleType,
    action: ActionType,
    options?: {
      resourceType?: ResourceType;
      resourceId?: string;
      resourceName?: string;
      metadata?: Record<string, any>;
    }
  ): Promise<void> {
    try {
      await this.initialize();

      if (!this.currentUserId) {
        console.warn('Cannot log activity: No user ID available');
        return;
      }

      const activityData: Partial<ActivityLog> = {
        user_id: this.currentUserId,
        organization_id: this.currentOrganizationId || undefined,
        module,
        action,
        resource_type: options?.resourceType,
        resource_id: options?.resourceId,
        resource_name: options?.resourceName,
        metadata: options?.metadata,
        user_agent: navigator.userAgent,
      };

      // Insert without waiting for response (fire and forget for performance)
      supabase.from('activity_logs').insert(activityData).then(({ error }) => {
        if (error) {
          console.error('Failed to log activity:', error);
        }
      });
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  }

  /**
   * Log page navigation
   */
  async logNavigation(module: ModuleType, pageName?: string) {
    await this.logActivity(module, 'navigate', {
      metadata: { page: pageName },
    });
  }

  /**
   * Log resource view
   */
  async logView(
    module: ModuleType,
    resourceType: ResourceType,
    resourceId: string,
    resourceName?: string
  ) {
    await this.logActivity(module, 'view', {
      resourceType,
      resourceId,
      resourceName,
    });
  }

  /**
   * Log search/filter operations
   */
  async logSearch(module: ModuleType, searchTerm: string, filters?: Record<string, any>) {
    await this.logActivity(module, 'search', {
      metadata: { searchTerm, filters },
    });
  }

  /**
   * Get activities with filters
   */
  async getActivities(filters?: ActivityFilters): Promise<ActivityLog[]> {
    try {
      let query = supabase.from('activity_logs').select('*');

      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }

      if (filters?.organizationId) {
        query = query.eq('organization_id', filters.organizationId);
      }

      if (filters?.module) {
        if (Array.isArray(filters.module)) {
          query = query.in('module', filters.module);
        } else {
          query = query.eq('module', filters.module);
        }
      }

      if (filters?.action) {
        if (Array.isArray(filters.action)) {
          query = query.in('action', filters.action);
        } else {
          query = query.eq('action', filters.action);
        }
      }

      if (filters?.resourceType) {
        query = query.eq('resource_type', filters.resourceType);
      }

      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate.toISOString());
      }

      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate.toISOString());
      }

      query = query.order('created_at', { ascending: false });

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching activities:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching activities:', error);
      return [];
    }
  }

  /**
   * Get user activity summary
   */
  async getUserActivitySummary(
    userId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<UserActivitySummary | null> {
    try {
      const start = startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
      const end = endDate || new Date();

      // Fetch all activities for the user in the date range
      const activities = await this.getActivities({
        userId,
        startDate: start,
        endDate: end,
      });

      if (activities.length === 0) {
        return null;
      }

      // Calculate module breakdown
      const moduleBreakdown: Record<string, number> = {};
      const actionBreakdown: Record<string, number> = {};
      const uniqueDays = new Set<string>();

      activities.forEach((activity) => {
        // Module breakdown
        moduleBreakdown[activity.module] = (moduleBreakdown[activity.module] || 0) + 1;

        // Action breakdown
        actionBreakdown[activity.action] = (actionBreakdown[activity.action] || 0) + 1;

        // Track active days
        const day = new Date(activity.created_at).toISOString().split('T')[0];
        uniqueDays.add(day);
      });

      // Get most used modules
      const mostUsedModules = Object.entries(moduleBreakdown)
        .map(([module, count]) => ({ module: module as ModuleType, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Get most common actions
      const mostCommonActions = Object.entries(actionBreakdown)
        .map(([action, count]) => ({ action: action as ActionType, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Get recent activities
      const recentActivities = activities.slice(0, 10);

      return {
        userId,
        totalActivities: activities.length,
        moduleBreakdown: moduleBreakdown as Record<ModuleType, number>,
        actionBreakdown: actionBreakdown as Record<ActionType, number>,
        mostUsedModules,
        mostCommonActions,
        recentActivities,
        firstActivity: activities[activities.length - 1]?.created_at || '',
        lastActivity: activities[0]?.created_at || '',
        activeDays: uniqueDays.size,
      };
    } catch (error) {
      console.error('Error fetching user activity summary:', error);
      return null;
    }
  }

  /**
   * Get organization-wide activity statistics
   */
  async getOrganizationActivityStats(organizationId: string, startDate?: Date, endDate?: Date) {
    try {
      const start = startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const end = endDate || new Date();

      const activities = await this.getActivities({
        organizationId,
        startDate: start,
        endDate: end,
      });

      // Calculate statistics
      const userActivity = new Map<string, number>();
      const moduleUsage = new Map<ModuleType, number>();

      activities.forEach((activity) => {
        userActivity.set(activity.user_id, (userActivity.get(activity.user_id) || 0) + 1);
        moduleUsage.set(
          activity.module,
          (moduleUsage.get(activity.module) || 0) + 1
        );
      });

      return {
        totalActivities: activities.length,
        uniqueUsers: userActivity.size,
        topUsers: Array.from(userActivity.entries())
          .map(([userId, count]) => ({ userId, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10),
        moduleUsage: Array.from(moduleUsage.entries())
          .map(([module, count]) => ({ module, count }))
          .sort((a, b) => b.count - a.count),
      };
    } catch (error) {
      console.error('Error fetching organization activity stats:', error);
      return null;
    }
  }

  /**
   * Get activity timeline data for charts
   */
  async getActivityTimeline(
    userId: string,
    days: number = 30
  ): Promise<Array<{ date: string; count: number }>> {
    try {
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      const activities = await this.getActivities({
        userId,
        startDate,
      });

      // Group by date
      const dateMap = new Map<string, number>();
      activities.forEach((activity) => {
        const date = new Date(activity.created_at).toISOString().split('T')[0];
        dateMap.set(date, (dateMap.get(date) || 0) + 1);
      });

      // Fill in missing dates with 0
      const timeline: Array<{ date: string; count: number }> = [];
      for (let i = 0; i < days; i++) {
        const date = new Date(Date.now() - (days - 1 - i) * 24 * 60 * 60 * 1000);
        const dateStr = date.toISOString().split('T')[0];
        timeline.push({
          date: dateStr,
          count: dateMap.get(dateStr) || 0,
        });
      }

      return timeline;
    } catch (error) {
      console.error('Error fetching activity timeline:', error);
      return [];
    }
  }
}

export const activityTrackingService = new ActivityTrackingService();
